import { memo, useCallback } from 'react';
import { Input, Label } from '@flote/ui';
import type { RecipientState } from '../hooks';

interface RecipientInputProps {
  recipient: RecipientState;
  onRecipientChange: (field: keyof RecipientState, value: string) => void;
  errors?: Record<string, string>;
  errorPrefix?: string;
  disabled?: boolean;
  nameLabel?: string;
  namePlaceholder?: string;
  phoneLabel?: string;
  phonePlaceholder?: string;
  nameRequired?: boolean;
}

function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

export const RecipientInput = memo<RecipientInputProps>(({
  recipient,
  onRecipientChange,
  errors = {},
  errorPrefix = '',
  disabled = false,
  nameLabel = 'Name',
  namePlaceholder = 'John Doe',
  phoneLabel = 'Phone',
  phonePlaceholder = '(555) 123-4567',
  nameRequired = true,
}) => {
  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    onRecipientChange('phone', formatted);
  }, [onRecipientChange]);

  const getError = (field: string) => errors[`${errorPrefix}${field}`];
  const nameError = getError('name');

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor={`${errorPrefix}recipientName`}>
          {nameLabel} {nameRequired && '*'}
        </Label>
        <Input
          id={`${errorPrefix}recipientName`}
          placeholder={namePlaceholder}
          value={recipient.name}
          onChange={(e) => onRecipientChange('name', e.target.value)}
          disabled={disabled}
          className={nameError ? 'border-destructive' : ''}
        />
        {nameError && (
          <p className="text-xs text-destructive">{nameError}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${errorPrefix}recipientPhone`}>{phoneLabel}</Label>
        <Input
          id={`${errorPrefix}recipientPhone`}
          type="tel"
          placeholder={phonePlaceholder}
          value={recipient.phone}
          onChange={handlePhoneChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
});

RecipientInput.displayName = 'RecipientInput';
